// Enhanced User Management with Modern Design
import React, { useEffect, useState } from 'react';
import { FaUsers, FaCog, FaTrash, FaEdit, FaEye, FaShieldAlt, FaSearch, FaFilter, FaCheck, FaExclamationTriangle, FaClock, FaUser } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editGroups, setEditGroups] = useState([]);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [message, setMessage] = useState(null);

  const [showIPs, setShowIPs] = useState(false);
  const [ipUser, setIpUser] = useState(null);

  // Validation functions
  const validateUsername = (username, isEditing = false) => {
    const errors = [];
    if (!username || username.length < 3) {
      errors.push('Benutzername muss mindestens 3 Zeichen lang sein');
    }
    if (username.length > 30) {
      errors.push('Benutzername darf maximal 30 Zeichen lang sein');
    }
    // Skip pattern validation when editing existing users - allow any characters for admins
    if (!isEditing && !/^[a-zA-Z0-9_-]+$/.test(username)) {
      errors.push('Benutzername darf nur Buchstaben, Zahlen, Unterstriche und Bindestriche enthalten');
    }
    return errors;
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password && password.length < 6) {
      errors.push('Passwort muss mindestens 6 Zeichen lang sein');
    }
    if (password && password.length > 128) {
      errors.push('Passwort darf maximal 128 Zeichen lang sein');
    }
    return errors;
  };

  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    fetchData();
  }, [onlyInactive, page, query, selectedGroup]);

  const fetchData = () => {
    setLoading(true);
    axiosInstance.get('/users/paged', {
      params: { page, limit, query, onlyInactive, group: selectedGroup }
    }).then(res => {
      setUsers(res.data.users);
      setTotal(res.data.total);
      setLoading(false);
    }).catch(err => {
      console.error('Error loading users:', err);
      setLoading(false);
    });
    
    axiosInstance.get('/admin/groups').then(res => {
      setGroups(res.data);
    }).catch(err => {
      console.error('Error loading groups:', err);
    });
  };

  const toggleActive = (userId, newStatus) => {
    axiosInstance.put(`/users/${userId}`, { active: newStatus }).then(res => {
      setUsers(prev => prev.map(u => (u._id === userId ? res.data.user : u)));
    });
  };

  const handleDelete = (userId) => {
    if (window.confirm('Bist du sicher, dass du diesen User löschen willst?')) {
      axiosInstance.delete(`/users/${userId}`).then(() => {
        fetchData();
      });
    }
  };

  const handleEditClick = async (user) => {
    try {
      const res = await axiosInstance.get(`/admin/users/${user._id}`);
      setEditUser(res.data.user);
      setEditGroups(user.groups?.map(g => g._id) || []);
      setEditIsAdmin(user.isAdmin);
      setEditUsername(user.username);
      setEditPassword('');
      setShowEdit(true);
    } catch (err) {
      console.error('Fehler beim Laden des Users:', err);
      setMessage({ type: 'error', text: 'Fehler beim Laden des Benutzers.' });
    }
  };

  const handleShowIPs = (user) => {
    setIpUser(user);
    setShowIPs(true);
  };

  const handleSave = async () => {
    console.log('handleSave called!');
    if (!editUser) {
      console.log('No editUser found!');
      return;
    }
    
    console.log('Current editUser:', editUser);
    
    // Validate input - allow any characters when editing existing users
    const usernameErrors = validateUsername(editUsername, true);
    const passwordErrors = validatePassword(editPassword);
    
    if (usernameErrors.length > 0 || passwordErrors.length > 0) {
      const allErrors = [...usernameErrors, ...passwordErrors];
      setMessage({ type: 'error', text: allErrors.join(', ') });
      return;
    }
    
    try {
      const hasUsernameChange = editUsername !== editUser.username;
      const hasPasswordChange = editPassword.length > 0;
      const hasGroupChange = JSON.stringify(editGroups.sort()) !== JSON.stringify((editUser.groups?.map(g => g._id) || []).sort());
      const hasAdminChange = editIsAdmin !== editUser.isAdmin;
      
      // Prepare update data
      const updateData = {};
      const changes = [];
      
      if (hasUsernameChange) {
        updateData.username = editUsername;
        changes.push('Benutzername');
      }
      
      if (hasPasswordChange) {
        updateData.password = editPassword;
        changes.push('Passwort');
      }
      
      if (hasGroupChange) {
        updateData.groups = editGroups;
        changes.push('Gruppen');
      }
      
      if (hasAdminChange) {
        updateData.isAdmin = editIsAdmin;
        changes.push('Admin-Status');
      }
      
      // If no changes were detected, still send current values to allow admin override
      if (!hasUsernameChange && !hasPasswordChange && !hasGroupChange && !hasAdminChange) {
        updateData.username = editUsername;
        updateData.groups = editGroups;
        updateData.isAdmin = editIsAdmin;
        changes.push('Daten bestätigt');
      }
      
      console.log('Sending updateData:', updateData);
      
      const response = await axiosInstance.put(`/users/${editUser._id}`, updateData);
      
      // Show success message
      if (response.data?.changes && response.data.changes.length > 0) {
        setMessage({ 
          type: 'success', 
          text: `Benutzer erfolgreich aktualisiert: ${response.data.changes.join(', ')}` 
        });
      } else {
        setMessage({ 
          type: 'success', 
          text: `${changes.join(', ')} erfolgreich aktualisiert!` 
        });
      }
      
      // Update local user state if response contains updated user
      if (response.data?.user) {
        setUsers(prev => prev.map(u => u._id === editUser._id ? response.data.user : u));
      }
      
      setShowEdit(false);
      setEditUser(null);
      setEditUsername('');
      setEditPassword('');
      setEditGroups([]);
      setEditIsAdmin(false);
      fetchData();
    } catch (err) {
      console.error('Save error:', err);
      const errorMsg = err.response?.data?.error || 'Fehler beim Aktualisieren des Benutzers.';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const pageCount = Math.ceil(total / limit);
  const paginationWindow = 3;
  const paginationButtons = [];
  const start = Math.max(1, page - paginationWindow);
  const end = Math.min(pageCount, page + paginationWindow);
  for (let i = start; i <= end; i++) {
    paginationButtons.push(i);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <div className="max-w-7xl mx-auto p-6">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
              <FaUsers className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Benutzerverwaltung</h1>
              <p className="text-blue-200 text-lg">Verwalten Sie Benutzerkonten und Berechtigungen</p>
            </div>
          </div>
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full"></div>
        </div>

        {/* Alert Messages */}
        {message && (
          <div className={`alert mb-6 shadow-lg ${message.type === 'success' ? 'alert-success' : message.type === 'warning' ? 'alert-warning' : 'alert-error'}`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' && <FaCheck className="text-lg" />}
              {message.type === 'error' && <FaExclamationTriangle className="text-lg" />}
              {message.type === 'warning' && <FaClock className="text-lg" />}
              <span className="font-medium">{message.text}</span>
            </div>
          </div>
        )}

        {/* Enhanced Filter Section */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 shadow-xl border border-white/20">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <label className="block text-sm font-semibold text-white mb-2">
                <FaSearch className="inline mr-2" />
                Benutzer suchen
              </label>
              <input
                type="text"
                placeholder="Nach Benutzername oder E-Mail suchen..."
                className="input input-bordered w-full bg-white/20 text-white placeholder-white/60 border-white/30 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
            
            <div className="min-w-48">
              <label className="block text-sm font-semibold text-white mb-2">
                <FaFilter className="inline mr-2" />
                Gruppe filtern
              </label>
              <select
                className="select select-bordered w-full bg-white/20 text-white border-white/30 focus:border-blue-400"
                value={selectedGroup}
                onChange={(e) => { setSelectedGroup(e.target.value); setPage(1); }}
              >
                <option value="" className="text-gray-800">Alle Gruppen</option>
                {groups.map(group => (
                  <option key={group._id} value={group._id} className="text-gray-800">{group.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className="form-control">
                <label className="label cursor-pointer flex-col items-center gap-2">
                  <span className="label-text text-white font-semibold text-sm">Nur inaktive</span>
                  <input 
                    type="checkbox" 
                    className="toggle toggle-primary" 
                    checked={onlyInactive} 
                    onChange={() => { setOnlyInactive(!onlyInactive); setPage(1); }} 
                  />
                </label>
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex gap-4 mt-4 pt-4 border-t border-white/20">
            <div className="stat bg-white/10 rounded-xl flex-1">
              <div className="stat-title text-white/80">Gesamt gefunden</div>
              <div className="stat-value text-white text-2xl">{total}</div>
            </div>
            <div className="stat bg-white/10 rounded-xl flex-1">
              <div className="stat-title text-white/80">Aktuelle Seite</div>
              <div className="stat-value text-white text-2xl">{page} / {pageCount}</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <span className="loading loading-spinner loading-lg text-blue-400"></span>
              <p className="text-white/80">Lade Benutzerdaten...</p>
            </div>
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  <tr>
                    <th className="font-semibold text-white">
                      <FaUser className="inline mr-2" />
                      Benutzername
                    </th>
                    <th className="font-semibold text-white">E-Mail</th>
                    <th className="font-semibold text-white">Gruppen</th>
                    <th className="font-semibold text-white">E-Mail bestätigt</th>
                    <th className="font-semibold text-white">Status</th>
                    <th className="font-semibold text-white">Admin</th>
                    <th className="font-semibold text-white">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white/5">
                  {users.map((user, index) => (
                    <tr key={user._id} className={`hover:bg-white/10 transition-colors ${index % 2 === 0 ? 'bg-white/5' : 'bg-transparent'}`}>
                      <td className="font-mono text-sm text-white font-semibold">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          {user.username}
                        </div>
                      </td>
                      <td className="text-white/90">{user.email}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {user.groups?.map(g => (
                            <div key={g._id} className="badge badge-secondary badge-sm">{g.name}</div>
                          ))}
                        </div>
                      </td>
                      <td className="text-center">
                        {user.emailVerified ? 
                          <span className="text-green-400 text-lg">✅</span> : 
                          <span className="text-red-400 text-lg">❌</span>
                        }
                      </td>
                      <td>
                        <button
                          className={`btn btn-xs ${user.active ? 'btn-success' : 'btn-error'}`}
                          onClick={() => toggleActive(user._id, !user.active)}
                        >
                          {user.active ? 'Aktiv' : 'Deaktiviert'}
                        </button>
                      </td>
                      <td className="text-center">
                        {user.isAdmin ? 
                          <span className="text-yellow-400 text-lg">👑</span> : 
                          <span className="text-gray-400 text-lg">👤</span>
                        }
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          <button 
                            className="btn btn-warning btn-xs flex items-center gap-1" 
                            onClick={() => handleEditClick(user)}
                          >
                            <FaEdit className="text-xs" />
                            Bearbeiten
                          </button>
                          <button 
                            className="btn btn-error btn-xs flex items-center gap-1" 
                            onClick={() => handleDelete(user._id)}
                          >
                            <FaTrash className="text-xs" />
                            Löschen
                          </button>
                          <button 
                            className="btn btn-info btn-xs flex items-center gap-1" 
                            onClick={() => handleShowIPs(user)}
                          >
                            <FaEye className="text-xs" />
                            IPs
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            <div className="p-4 flex justify-center border-t border-white/10">
              <div className="join bg-white/10 backdrop-blur-md rounded-xl p-1">
                {page > 1 && (
                  <button className="join-item btn btn-sm bg-white/20 text-white border-white/30 hover:bg-white/30" onClick={() => setPage(page - 1)}>
                    ‹ Zurück
                  </button>
                )}
                {paginationButtons.map((i) => (
                  <button
                    key={i}
                    className={`join-item btn btn-sm ${page === i ? 'bg-blue-500 text-white border-blue-400' : 'bg-white/20 text-white border-white/30 hover:bg-white/30'}`}
                    onClick={() => setPage(i)}
                  >
                    {i}
                  </button>
                ))}
                {page < pageCount && (
                  <button className="join-item btn btn-sm bg-white/20 text-white border-white/30 hover:bg-white/30" onClick={() => setPage(page + 1)}>
                    Weiter ›
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Edit User Modal */}
        {showEdit && editUser && (
          <div className="modal modal-open">
            <div className="modal-box max-w-2xl bg-gradient-to-br from-white to-blue-50">
              <h3 className="font-bold text-2xl mb-6 text-gray-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <FaEdit className="text-white" />
                </div>
                Benutzer bearbeiten
              </h3>
              
              {/* User Info Display */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-blue-200">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {editUser.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-xl text-gray-800">{editUser.username}</div>
                    <div className="text-gray-600">{editUser.email}</div>
                    <div className="flex items-center gap-2 mt-2">
                      {editUser.isAdmin && (
                        <span className="badge badge-warning badge-sm">Administrator</span>
                      )}
                      {editUser.emailVerified ? (
                        <span className="badge badge-success badge-sm">E-Mail bestätigt</span>
                      ) : (
                        <span className="badge badge-error badge-sm">E-Mail nicht bestätigt</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
                {/* Username Field */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold text-gray-700">Benutzername</span>
                  </label>
                  <input
                    type="text"
                    value={editUsername}
                    onChange={(e) => setEditUsername(e.target.value)}
                    className={`input input-bordered w-full ${
                      validateUsername(editUsername, true).length > 0 ? 'input-error' : 'input-primary'
                    }`}
                    placeholder="Benutzername eingeben"
                    required
                  />
                  <label className="label">
                    <span className="label-text-alt text-blue-600">
                      3-30 Zeichen - Admin-Bearbeitung erlaubt alle Zeichen
                    </span>
                  </label>
                  {validateUsername(editUsername, true).length > 0 && (
                    <div className="text-error text-sm mt-1">
                      {validateUsername(editUsername, true).map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Password Field */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold text-gray-700">Neues Passwort (Optional)</span>
                  </label>
                  <input
                    type="password"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    className={`input input-bordered w-full ${
                      validatePassword(editPassword).length > 0 ? 'input-error' : 'input-primary'
                    }`}
                    placeholder="Leer lassen, um Passwort nicht zu ändern"
                  />
                  <label className="label">
                    <span className="label-text-alt text-blue-600">
                      {editPassword ? 'Mindestens 6 Zeichen' : 'Leer lassen für keine Änderung'}
                    </span>
                  </label>
                  {validatePassword(editPassword).length > 0 && (
                    <div className="text-error text-sm mt-1">
                      {validatePassword(editPassword).map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Admin Rights Toggle */}
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-4">
                    <div className="flex items-center gap-2">
                      <FaShieldAlt className="text-orange-500 text-lg" />
                      <span className="label-text font-semibold text-gray-700">Administrator-Rechte</span>
                    </div>
                    <input
                      type="checkbox"
                      className="toggle toggle-warning"
                      checked={editIsAdmin}
                      onChange={(e) => setEditIsAdmin(e.target.checked)}
                    />
                  </label>
                </div>

                {/* Groups Selection */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold text-gray-700">Gruppen ({editGroups.length} ausgewählt)</span>
                  </label>
                  <div className="bg-base-100 rounded-lg p-4 max-h-48 overflow-y-auto border-2 border-base-300">
                    {groups.length === 0 ? (
                      <div className="text-gray-500 text-center py-4">Keine Gruppen verfügbar</div>
                    ) : (
                      <div className="space-y-2">
                        {groups.map(group => (
                          <label key={group._id} className="flex items-center gap-3 cursor-pointer hover:bg-base-200 p-2 rounded-lg">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary checkbox-sm"
                              checked={editGroups.includes(group._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditGroups([...editGroups, group._id]);
                                } else {
                                  setEditGroups(editGroups.filter(id => id !== group._id));
                                }
                              }}
                            />
                            <span className="font-medium">{group.name}</span>
                            {group.description && (
                              <span className="text-xs text-gray-500">- {group.description}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setEditGroups(groups.map(g => g._id))}
                      className="btn btn-sm btn-outline btn-primary"
                    >
                      Alle auswählen
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditGroups([])}
                      className="btn btn-sm btn-outline btn-secondary"
                    >
                      Alle abwählen
                    </button>
                  </div>
                </div>
              </form>
              
              {/* Modal Actions */}
              <div className="modal-action pt-6 border-t border-gray-200">
                <button 
                  onClick={() => {
                    setShowEdit(false);
                    setEditUser(null);
                    setEditUsername('');
                    setEditPassword('');
                    setEditGroups([]);
                    setEditIsAdmin(false);
                  }}
                  className="btn btn-ghost"
                  disabled={loading}
                >
                  Abbrechen
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('Button clicked!');
                    handleSave();
                  }}
                  disabled={
                    loading || 
                    validateUsername(editUsername, true).length > 0 ||
                    validatePassword(editPassword).length > 0
                  }
                  className="btn btn-primary"
                  type="button"
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Speichert...
                    </>
                  ) : (
                    <>
                      <FaCog />
                      Änderungen speichern
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* IP History Modal */}
        {showIPs && ipUser && (
          <div className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <FaEye className="text-blue-500" />
                Login-History von {ipUser?.username}
              </h3>
              <div className="py-4">
                {ipUser?.loginHistory?.length > 0 ? (
                  <div className="space-y-2">
                    {ipUser.loginHistory.map((entry, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-base-200 rounded">
                        <span className="font-mono text-sm">{entry.ip}</span>
                        <span className="text-sm text-gray-500">{new Date(entry.date).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Keine Login-History vorhanden.
                  </div>
                )}
              </div>
              <div className="modal-action">
                <button className="btn btn-primary" onClick={() => setShowIPs(false)}>
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
